import { resolveApiBaseUrl } from "@/lib/api/base-url";
import { extractApiErrorMessage } from "@/lib/api/errors";
import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { onAnalyticsReady, trackEvent } from "@/lib/analytics";
import { consultantApplicationApi, sessionManager } from "@/lib/api/consultant-application";
import { parseCodeExpiryMinutes } from "@/lib/verificationCode";
import {
  captureResumeToken,
  clearCapturedResumeToken,
  getCapturedResumeToken,
} from "@/lib/resumeLink";
import { captureShareToken, getShareToken } from "@/lib/shareLink";
import type {
  ApplicationFormData,
  ApplicationSubmitRequest,
  ApplicationState,
  ConsultantApplicationStepData,
} from "@/types/consultant-application";
import type { SkillSearchResult } from "@/components/skills/SkillsAutocomplete";
import { normalizeLinkedInProfileUrl } from "@/lib/validation/linkedin";
import { resolveRateCurrency, toRateCurrency } from "@/lib/rateCurrency";

const initialFormData: ApplicationFormData = {
  // Step 1 - Basic Info
  fullName: "",
  email: "",
  phone: "",

  // Step 2 - Company Info
  companyName: "",
  billingEmail: "",
  cvrNumber: "",
  address: "",
  postalCode: "",
  city: "",
  country: "",
  website: "",

  // Step 3 - Profile & Skills
  bio: "",
  linkedinUrl: "",
  yearsExperience: null,
  hourlyRate: null,
  hourlyRateCurrency: "",
  interestedInEmployment: false,
  selectedSkills: [],

  // CV Document
  cvUrl: null,
};

const initialState: ApplicationState = {
  sessionId: null,
  currentStep: 1,
  emailVerified: false,
  formData: initialFormData,
  isRestoringSession: false,
  verificationEmail: null,
  codeExpiresInMinutes: null,
  isLoading: false,
  errors: {},
  lastSaved: null,
};

/**
 * `sessionStorage` reads throw a `SecurityError` where storage access is denied
 * (strict privacy settings, some embedded contexts). This read now also happens
 * during render, where an exception would stop the public signup page mounting
 * at all — so treat an unreadable store as "no stored session" and start fresh.
 */
const readStoredSessionId = (): string | null => {
  try {
    return sessionManager.getSessionId();
  } catch (error) {
    console.warn("Could not read the stored application session:", error);
    return null;
  }
};

/** The resume token the app was opened with, if any (backend#901). */
const readResumeToken = (): string | null => {
  // Idempotent: main.tsx normally captured it at boot already.
  captureResumeToken();
  return getCapturedResumeToken();
};

/**
 * Exchange a reminder's resume token for the signup session (backend#901) and
 * keep it like a session this tab started. Null when the link is invalid or
 * expired.
 */
const exchangeResumeToken = async (token: string): Promise<string | null> => {
  try {
    const { session_id: sessionId } = await consultantApplicationApi.resumeApplication(token);
    try {
      sessionManager.setSessionId(sessionId);
    } catch (error) {
      // Unwritable storage only costs a later reload; this visit still resumes.
      console.warn("Could not store the resumed application session:", error);
    }
    return sessionId;
  } catch {
    return null;
  }
};

export const useConsultantApplication = () => {
  // Read once per mount, so a re-run of the effect (StrictMode) resumes too.
  // The token stays valid until it expires, so exchanging it twice is safe.
  const [resumeToken] = useState(readResumeToken);

  // A stored session id or a resume link means a restore is about to run.
  // Starting in the restoring state keeps the wizard steps from mounting — and
  // snapshotting empty defaults into their forms — before the restored data
  // arrives (#873).
  const [state, setState] = useState<ApplicationState>(() => ({
    ...initialState,
    isRestoringSession: readStoredSessionId() !== null || resumeToken !== null,
  }));

  // Load existing session on mount
  useEffect(() => {
    // Set by the cleanup: an effect run that was superseded (StrictMode's
    // second run, or an unmount) must not write state or toast.
    let cancelled = false;

    const loadExistingSession = async () => {
      // A resume link from a reminder email wins over a session this tab
      // happens to hold: it is the application the applicant came back for.
      let resumedSessionId: string | null = null;
      if (resumeToken) {
        resumedSessionId = await exchangeResumeToken(resumeToken);
        if (cancelled) {
          return;
        }
        clearCapturedResumeToken();
        if (!resumedSessionId) {
          // Start fresh, as the message says: never fall back to an unrelated
          // draft this tab happens to hold.
          toast.error(
            "This link has expired or was replaced by a newer email. Please start a new application or contact support@bondit.dk."
          );
          setState(prev => ({ ...prev, isRestoringSession: false }));
          return;
        }
      }
      const sessionId = resumedSessionId ?? readStoredSessionId();
      if (!sessionId) {
        setState(prev => ({ ...prev, isRestoringSession: false }));
        return;
      }

      try {
        setState(prev => ({ ...prev, isLoading: true }));
        const status = await consultantApplicationApi.getApplicationStatus(sessionId);
        if (cancelled) {
          return;
        }

        // Convert API response to form data
        const formData: ApplicationFormData = {
          fullName: status.step_data.full_name || "",
          email: status.step_data.email || "",
          phone: status.step_data.phone || "",
          companyName: status.step_data.company_name || "",
          billingEmail: status.step_data.company_billing_email || "",
          cvrNumber: status.step_data.cvr_number || "",
          address: status.step_data.address || "",
          postalCode: status.step_data.postal_code || "",
          city: status.step_data.city || "",
          country: status.step_data.country || "",
          website: status.step_data.website || "",
          bio: status.step_data.bio || "",
          linkedinUrl: status.step_data.linkedin_url || "",
          // `??` — a saved 0 is a value, not "missing" (#891)
          yearsExperience: status.step_data.years_experience ?? null,
          hourlyRate: status.step_data.hourly_rate ?? null,
          // A draft saved before backend#883 has no currency: "" lets the
          // country default apply until the applicant picks one.
          hourlyRateCurrency: toRateCurrency(status.step_data.hourly_rate_currency) ?? "",
          interestedInEmployment: status.step_data.interested_in_employment === true,
          selectedSkills: [], // Will be loaded separately if needed
          // cv_url lives on the application record, not in step_data — the
          // old read (`step_data.cv_url`) was always undefined, so uploaded
          // CVs never restored on session resume.
          cvUrl: status.cv_url || null,
        };

        setState(prev => ({
          ...prev,
          sessionId: status.session_id,
          currentStep: status.current_step ?? 1,
          emailVerified: status.email_verified,
          formData,
          // A restored session's code went to the address it was started with.
          verificationEmail: formData.email || null,
          isRestoringSession: false,
          isLoading: false,
          lastSaved: new Date(status.updated_at),
        }));

        toast.success("Previous application session restored");
      } catch {
        if (cancelled) {
          return;
        }
        // Session expired or invalid, start fresh. The remove is itself
        // guarded: it runs inside the catch, so an exception here (storage
        // readable but not writable) would escape before the state reset and
        // leave the wizard stuck on "Restoring your application…" forever —
        // a dead public signup page. Clearing the flag matters more than
        // clearing the id.
        try {
          sessionManager.removeSessionId();
        } catch (error) {
          console.warn("Could not clear the stored application session:", error);
        }
        setState(prev => ({ ...prev, isRestoringSession: false, isLoading: false }));
      }
    };

    loadExistingSession();
    return () => {
      cancelled = true;
    };
  }, [resumeToken]);

  // `signup_step` once per step the applicant is shown (#911). The step only
  // changes after its save resolved (or a stored session restored), so this
  // reports reached steps, not clicks. Waiting out the restore keeps the
  // placeholder step 1 of a resuming applicant from being reported, and the
  // ref stops StrictMode's double effect run and unrelated re-renders from
  // sending the same step twice. A step only counts as reported once
  // `trackEvent` took it: if the applicant accepts analytics while a step is
  // on screen, the ready signal re-runs this and reports that step.
  const trackedStepRef = useRef<number | null>(null);
  const [analyticsReadyCount, setAnalyticsReadyCount] = useState(0);
  useEffect(() => onAnalyticsReady(() => setAnalyticsReadyCount(count => count + 1)), []);
  useEffect(() => {
    if (state.isRestoringSession || trackedStepRef.current === state.currentStep) {
      return;
    }
    if (trackEvent("signup_step", { step: state.currentStep })) {
      trackedStepRef.current = state.currentStep;
    }
  }, [state.currentStep, state.isRestoringSession, analyticsReadyCount]);

  // Update form data
  const updateFormData = useCallback((updates: Partial<ApplicationFormData>) => {
    // Convert string values back to numbers for years and rate if they are strings.
    // "0" is a value, not "missing" (#891) — only an empty or non-numeric
    // string becomes null.
    const processedUpdates = { ...updates };
    const toNumberOrNull = (value: string): number | null => {
      if (value === "") {
        return null;
      }
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };

    if ("yearsExperience" in updates) {
      const value = updates.yearsExperience;
      if (typeof value === "string") {
        processedUpdates.yearsExperience = toNumberOrNull(value);
      }
    }

    if ("hourlyRate" in updates) {
      const value = updates.hourlyRate;
      if (typeof value === "string") {
        processedUpdates.hourlyRate = toNumberOrNull(value);
      }
    }

    setState(prev => ({
      ...prev,
      formData: { ...prev.formData, ...processedUpdates },
      errors: {}, // Clear errors when user makes changes
    }));
  }, []);

  // Set loading state
  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  // Set error
  const setError = useCallback((key: string, message: string) => {
    setState(prev => ({
      ...prev,
      errors: { ...prev.errors, [key]: message },
    }));
  }, []);

  // Clear errors
  const clearErrors = useCallback(() => {
    setState(prev => ({ ...prev, errors: {} }));
  }, []);

  // Start application (Step 1)
  const startApplication = useCallback(
    async (basicInfo: { fullName: string; email: string; phone: string }) => {
      try {
        setLoading(true);
        clearErrors();

        // Arrived from a shared assignment link: let the backend attribute
        // the signup to that share (#1054). Idempotent; main.tsx normally
        // captured it at boot already.
        captureShareToken();
        const share = getShareToken();

        const response = await consultantApplicationApi.startApplication({
          full_name: basicInfo.fullName,
          email: basicInfo.email,
          phone: basicInfo.phone,
          ...(share ? { share } : {}),
        });

        sessionManager.setSessionId(response.session_id);

        setState(prev => ({
          ...prev,
          sessionId: response.session_id,
          formData: { ...prev.formData, ...basicInfo },
          verificationEmail: basicInfo.email,
          // A new session (e.g. after changing the address) starts from what
          // this response reports, not from the previous session's value.
          codeExpiresInMinutes: parseCodeExpiryMinutes(response.expires_in_minutes),
          isLoading: false,
        }));

        toast.success("Verification code sent to your email");
      } catch (error) {
        let message = extractApiErrorMessage(error, "Failed to start application");

        // Handle case where application already exists for this email
        if (
          message.includes("active application already exists") ||
          message.includes("already exists")
        ) {
          message =
            "An application with this email already exists. Please use a different email address or contact support if you need to update your existing application.";
        }

        setError("application", message);
        setLoading(false);
        throw error;
      }
    },
    [setLoading, clearErrors, setError]
  );

  // Verify email
  const verifyEmail = useCallback(
    async (code: string) => {
      if (!state.sessionId) {
        throw new Error("No active session");
      }

      try {
        setLoading(true);
        clearErrors();

        await consultantApplicationApi.verifyEmail({
          session_id: state.sessionId,
          code,
        });

        setState(prev => ({
          ...prev,
          emailVerified: true,
          isLoading: false,
        }));

        trackEvent("signup_email_verified");
        toast.success("Email verified successfully");
      } catch (error) {
        const message = extractApiErrorMessage(error, "Invalid verification code");
        setError("verification", message);
        setLoading(false);
        throw error;
      }
    },
    [state.sessionId, setLoading, clearErrors, setError]
  );

  // Resend verification code. It deliberately leaves `isLoading` alone: the
  // verification screen has its own "Sending..." state, and the shared flag
  // would disable the code inputs and read "Verifying your code..." while a
  // resend is in flight (backend#832).
  const resendVerificationCode = useCallback(async () => {
    if (!state.sessionId) {
      throw new Error("No active session");
    }

    try {
      clearErrors();

      const response = await consultantApplicationApi.resendCode({
        session_id: state.sessionId,
      });

      const reported = parseCodeExpiryMinutes(response?.expires_in_minutes);
      if (reported !== null) {
        setState(prev => ({ ...prev, codeExpiresInMinutes: reported }));
      }

      toast.success("Verification code sent");
    } catch (error) {
      const message = extractApiErrorMessage(error, "Failed to send verification code");
      setError("resend", message);
      throw error;
    }
  }, [state.sessionId, clearErrors, setError]);

  // Navigate to next step
  const nextStep = useCallback(async () => {
    if (!state.sessionId) {
      throw new Error("No active session");
    }

    const currentStep = state.currentStep;
    const newStep = Math.min(currentStep + 1, 3);

    try {
      setLoading(true);
      clearErrors();

      // Save current step data before progressing
      let stepData: Partial<ConsultantApplicationStepData> = {};

      if (currentStep === 1) {
        // Moving from step 1 to step 2 - basic info should already be saved
        console.warn("Moving from step 1 to 2 - basic info already saved during start");
      } else if (currentStep === 2) {
        // Moving from step 2 to step 3 - save company info
        console.warn("Saving step 2 data before moving to step 3");
        stepData = {
          company_name: state.formData.companyName || undefined,
          company_billing_email: state.formData.billingEmail || undefined,
          cvr_number: state.formData.cvrNumber || undefined,
          address: state.formData.address || undefined,
          postal_code: state.formData.postalCode || undefined,
          city: state.formData.city || undefined,
          country: state.formData.country || undefined,
          website: state.formData.website || undefined,
        };

        await consultantApplicationApi.updateApplicationStep(
          state.sessionId,
          currentStep,
          stepData
        );
        console.warn("Step 2 data saved successfully");
      }

      // Update frontend state to new step
      setState(prev => ({
        ...prev,
        currentStep: newStep,
        isLoading: false,
      }));

      console.warn(`Successfully moved from step ${currentStep} to step ${newStep}`);
    } catch (error) {
      console.error("Failed to save step data:", error);
      setError("step_progress", extractApiErrorMessage(error, "Failed to save step data"));
      setLoading(false);
      throw error;
    }
  }, [state.currentStep, state.sessionId, state.formData, setLoading, clearErrors, setError]);

  // Navigate to previous step
  const previousStep = useCallback(() => {
    const newStep = Math.max(state.currentStep - 1, 1);
    setState(prev => ({
      ...prev,
      currentStep: newStep,
    }));
  }, [state.currentStep]);

  // Search skills
  const searchSkills = useCallback(async (query: string): Promise<SkillSearchResult[]> => {
    try {
      return await consultantApplicationApi.searchSkills(query);
    } catch (error) {
      console.error("Skills search failed:", error);
      return [];
    }
  }, []);

  // Submit application
  // `acknowledgements` carries the privacy policy and confidentiality
  // acceptance ticked on the last step, with the versions shown (#863).
  const submitApplication = useCallback(
    async (acknowledgements: ApplicationSubmitRequest) => {
      if (!state.sessionId) {
        throw new Error("No active session");
      }

      try {
        setLoading(true);
        clearErrors();

        console.warn("=== SUBMISSION DEBUG INFO ===");
        console.warn("API Base URL:", resolveApiBaseUrl());
        console.warn("Session ID:", state.sessionId);
        console.warn("Session ID length:", state.sessionId?.length);
        console.warn("Session ID valid?", !!state.sessionId && state.sessionId.length > 10);
        console.warn("Form data:", {
          fullName: state.formData.fullName,
          email: state.formData.email,
          phone: state.formData.phone,
          companyName: state.formData.companyName,
          cvrNumber: state.formData.cvrNumber,
          address: state.formData.address,
          postalCode: state.formData.postalCode,
          city: state.formData.city,
          country: state.formData.country,
          website: state.formData.website,
          bio: state.formData.bio,
          linkedinUrl: state.formData.linkedinUrl,
          yearsExperience: state.formData.yearsExperience,
          hourlyRate: state.formData.hourlyRate,
          selectedSkills: state.formData.selectedSkills,
          selectedSkillsCount: state.formData.selectedSkills.length,
        });

        // Validate required fields before submission
        if (!state.formData.fullName || !state.formData.email || !state.formData.phone) {
          console.error("Missing required basic info:", {
            fullName: !!state.formData.fullName,
            email: !!state.formData.email,
            phone: !!state.formData.phone,
          });
          throw new Error("Missing required basic information");
        }

        if (state.formData.selectedSkills.length === 0) {
          console.error("No skills selected");
          throw new Error("At least one skill must be selected");
        }

        // Prepare the final step data with all form information
        const finalStepData: ConsultantApplicationStepData = {
          // Step 1 - Basic Info (required)
          full_name: state.formData.fullName,
          email: state.formData.email,
          phone: state.formData.phone,

          // Step 2 - Company Info (all optional)
          company_name: state.formData.companyName || undefined,
          company_billing_email: state.formData.billingEmail || undefined,
          cvr_number: state.formData.cvrNumber || undefined,
          address: state.formData.address || undefined,
          postal_code: state.formData.postalCode || undefined,
          city: state.formData.city || undefined,
          country: state.formData.country || undefined,
          website: state.formData.website || undefined,

          // Step 3 - Profile & Skills (bio, years, rate and skills required — #891;
          // `??` keeps a legitimate 0 in the payload)
          bio: state.formData.bio || undefined,
          linkedin_url:
            normalizeLinkedInProfileUrl(state.formData.linkedinUrl) ??
            (state.formData.linkedinUrl?.trim() || undefined),
          years_experience: state.formData.yearsExperience ?? undefined,
          hourly_rate: state.formData.hourlyRate ?? undefined,
          // Required with the rate (backend#883): the explicit choice, or the
          // default for the step-2 country the selector was showing.
          hourly_rate_currency: resolveRateCurrency(
            state.formData.hourlyRateCurrency,
            state.formData.country
          ),
          // An unticked box is a "no", not "not stated" (backend#882).
          interested_in_employment: state.formData.interestedInEmployment,
          skill_ids: state.formData.selectedSkills.map(skill => skill.id),
        };

        console.warn("Final step data prepared:", finalStepData);
        console.warn("Skills debugging:");
        console.warn("  - Selected skills array:", state.formData.selectedSkills);
        console.warn(
          "  - Skills with structure:",
          state.formData.selectedSkills.map(skill => ({
            id: skill.id,
            name: skill.name,
            type_of_id: typeof skill.id,
          }))
        );
        console.warn("  - Skill IDs being sent:", finalStepData.skill_ids);
        console.warn("  - Skill IDs types:", finalStepData.skill_ids?.map(id => typeof id) || []);
        console.warn("================================");

        // Save step 3 before submitting. A failed save must not be swallowed: it
        // leaves the server's current_step at 2, so the submit would be rejected
        // with a generic "incomplete" message that names no cause (#891) — let
        // the real error (unverified email, expired session…) reach the form.
        await consultantApplicationApi.updateApplicationStep(state.sessionId, 3, finalStepData);

        // Submit the application
        const result = await consultantApplicationApi.submitApplication(
          state.sessionId,
          acknowledgements
        );
        trackEvent("signup_submitted");

        // Clear session after successful submission
        sessionManager.removeSessionId();

        setState(prev => ({
          ...prev,
          isLoading: false,
        }));

        toast.success(
          "Application submitted successfully! You will receive a confirmation email shortly."
        );

        return result;
      } catch (error) {
        console.error("Application submission failed:", error);
        const message = extractApiErrorMessage(error, "Failed to submit application");
        setError("submit", message);
        setLoading(false);
        throw error;
      }
    },
    [state.sessionId, state.formData, setLoading, clearErrors, setError]
  );

  return {
    state,
    updateFormData,
    startApplication,
    verifyEmail,
    resendVerificationCode,
    nextStep,
    previousStep,
    searchSkills,
    submitApplication,
    setError,
    clearErrors,
  };
};
